import { useChatCartMutationState } from '../hooks/useChatCartMutationState'
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useStore } from 'react-redux'
import { defineMessage, useIntl } from 'react-intl'
import { useOnlineStatus } from 'hooks/useOnlineStatus'
import { useAppSelector } from 'hooks/reduxHooks'
import usePrevious from 'hooks/usePrevious'
import GeoContext from 'contexts/geoContext'
import {
  clearTripActionTurn,
  showTripActionConfirmedSnackbar,
  showTripActionFailedSnackbar,
  showTripActionPendingSnackbar,
} from 'tripPlanner/components/Snackbar/tripActionSnackbar'
import { reportLog } from 'services/userMonitoring'
import type {
  HotelSearchResultsData,
  ExperienceSearchResultsData,
  HotelSearchErrorData,
  ExperienceSearchErrorData,
  ChatUiActionData,
  ManualItineraryDeleteRequest,
  ManualItineraryMoveRequest,
} from 'tripPlanner/api/types'
import { useManualItineraryDeleteMutation, useManualItineraryMoveMutation } from 'tripPlanner/hooks/api/chat'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { resetPendingExperienceDaySelections, syncPendingExperienceDaySelection } from 'tripPlanner/utils/pendingExperienceDaySelection'
import { buildOfferIdContext, buildRoomOptionContext, resolveOccupancyAgeThresholdsForSendMessage } from 'tripPlanner/assistant/utils/messageEnrichment'
import { buildItemMovedActivityEntry } from '../utils/buildItemMovedActivityEntry'
import { getAllOffers } from 'selectors/offerSelectors'
import type { AssistantOfferPageTurnAuthority } from 'tripPlanner/assistant/api/types'
import { useAssistantSurfaceSnapshot, type AssistantSurfaceSnapshotBuildResult } from 'tripPlanner/assistant/surfaceSnapshots/useAssistantSurfaceSnapshot'
import { buildTripPlannerSnapshot } from 'tripPlanner/assistant/surfaceSnapshots/buildTripPlannerSnapshot'
import { buildTripPlannerDegradedIdentity } from 'tripPlanner/assistant/surfaceSnapshots/tripPlannerSnapshotIdentity'
import { buildAssistantIdentityScopeKey } from 'tripPlanner/assistant/utils/assistantIdentityScopeKey'
import { useSurfaceActivityAccumulator } from './useSurfaceActivityAccumulator'
import { useCommittedStateAccumulator } from './useCommittedStateAccumulator'
import type { UserData } from '../types'
import { useChatCore } from 'tripPlanner/assistant/runtime/chatStream'
import { useItineraryUpdates } from '../hooks/useItineraryUpdates'
import { useTripChatCartReconciliation } from '../hooks/useTripChatCartReconciliation'
import type { AnonymousClaimTransfer } from 'tripPlanner/assistant/hooks/useAnonymousChatClaiming'
import { useTripPlannerEventBus, useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { ChatActionsContext, type ChatActionsValue } from './ChatActionsContext'
import { ChatStreamStatusContext, type ChatStreamStatusValue } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { ChatScrollContext, type ChatScrollValue } from './ChatScrollContext'
import { ChatUiActionContext, type ChatUiActionValue } from './ChatUiActionContext'
import { ChatTurnCompletionContext, type ChatTurnCompletionValue } from './ChatTurnCompletionContext'
import { ChatStreamMessagesContext, type ChatStreamMessagesValue } from './ChatStreamMessagesContext'
import { ItineraryContext, type ItineraryContextValue } from './ItineraryContext'
import { ChatFilesContext, type ChatFilesValue } from './ChatFilesContext'
import { StaffRewindContext, useStaffRewindValue } from 'tripPlanner/assistant/contexts/StaffRewindContext'
import { AddedItineraryItemContext, type AddedItineraryItemContextValue } from 'tripPlanner/assistant/contexts/AddedItineraryItemContext'
import { dismissPendingTripPlannerSnackbar } from 'tripPlanner/components/Snackbar/TripPlannerSnackbarHost'
import { useTurnAuthoritySnapshot } from './TurnAuthoritySnapshotContext'

export interface TripPlannerChatProviderProps {
  chatId: number | undefined
  /** True while the route names a chat that `useChatRouteSync` hasn't copied into `chatId` yet. */
  isChatIdSyncPending: boolean
  setChatIdForStreaming: (id: number | undefined) => void
  isDevMode: boolean
  /** Staff-only "rewind": true only when viewing another user's trip. Shows per-turn
   * inspect/fork affordances and allows itinerary time-travel. */
  canStaffRewind: boolean
  /** Navigate to a chat by id — used by staff "rewind" fork to open the new chat. */
  navigateToChat: (chatId: number) => void
  isLoggedIn: boolean
  orderIds?: Array<string>
  refetchChatList: () => Promise<unknown>
  waitForAnonymousChatClaim: (signal?: AbortSignal) => Promise<void>
  startChatTitleGeneration: (chatId: number, message: string) => void
  updateChatLastActivityTimestamp: (chatId: number, iso: string) => void
  updateChatMetadata: (chatId: number, name: string, updatedAt: string) => void
  userData: UserData
  chatKey: string
  anonymousClaimTransfer: AnonymousClaimTransfer | undefined
  children: React.ReactNode
}

const MOVED_ITEM_COUNT = defineMessage({
  id: 'rVeiKDAOd7CipAYi17N2',
  defaultMessage: '{moveCount, plural, one {# itinerary item} other {# itinerary items}}',
  description: 'Names how many itinerary items a manual move affected, shown under the trip planner snackbar heading',
})

export function TripPlannerChatProvider({ children, ...props }: TripPlannerChatProviderProps) {
  const eventBus = useTripPlannerEventBus()
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const store = useStore<App.State>()
  const [files, setFiles] = useState<Array<File>>([])
  const { waitForAnonymousChatClaim } = props

  const isStreamingRef = useRef(false)
  const activeAssistantMessageIdRef = useRef<string | undefined>(undefined)

  const isOnline = useOnlineStatus()

  // ── Wire streaming callbacks ──

  const onHotelSearchResults = useCallback((data: HotelSearchResultsData, generation: number) => {
    eventBus.dispatch({ type: 'hotel_search_results', data, generation })
  }, [eventBus])

  const onExperienceSearchResults = useCallback((data: ExperienceSearchResultsData, generation: number) => {
    eventBus.dispatch({ type: 'experience_search_results', data, generation })
  }, [eventBus])

  const onHotelSearchError = useCallback((data: HotelSearchErrorData, generation: number) => {
    eventBus.dispatch({ type: 'hotel_search_error', data, generation })
  }, [eventBus])

  const onExperienceSearchError = useCallback((data: ExperienceSearchErrorData, generation: number) => {
    eventBus.dispatch({ type: 'experience_search_error', data, generation })
  }, [eventBus])

  // Buffer UI actions in state so they survive the landing→chat transition.
  // MapContainer may not be mounted when the first ui_action arrives (its event
  // subscription isn't active yet). The context-based buffer lets MapContainer
  // pick up the pending action on mount.
  const [pendingUiAction, setPendingUiAction] = useState<ChatUiActionData | undefined>(undefined)

  const clearPendingUiAction = useCallback(() => {
    setPendingUiAction(undefined)
  }, [])

  const onUiAction = useCallback((data: ChatUiActionData) => {
    setPendingUiAction(data)
    eventBus.dispatch({ type: 'ui_action', data })
  }, [eventBus])

  // ── Itinerary domain ──

  const {
    itineraryData,
    isItineraryLoading,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    addedItemLink,
    handleItineraryUpdates,
    handleEnrichmentComplete: handleItineraryEnrichmentComplete,
    handleRefetchItinerary,
    refetchCurrentChatItinerary,
  } = useItineraryUpdates({
    chatId: props.chatId,
    isChatIdSyncPending: props.isChatIdSyncPending,
    isStreamingRef,
    activeAssistantMessageIdRef,
  })

  // ── Assistant surface snapshot (read tool) ──

  // TripPlannerChatProvider is the producer of ItineraryContext, not a descendant -- calling
  // useItineraryContext() here would throw, so resolveTurnAuthority below closes over the
  // local itineraryData instead. Calling useAssistantSurfaceSnapshot() directly (not
  // useAssistantSurface()) is safe -- each hook call gets independent useRef-backed caches --
  // and is the right layer, since useAssistantSurface bundles chat-history/claiming machinery
  // this provider already owns its own version of via useChatCore.
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const turnAuthoritySnapshotRef = useTurnAuthoritySnapshot()
  const { resolveSurfaceSnapshotAuthority, clearAssistantSurfaceSnapshotRefs } = useAssistantSurfaceSnapshot('trip_planner')
  const { drainSurfaceActivity, restoreDrainedActivity, clearSurfaceActivity, claimSubmittedActivity } = useSurfaceActivityAccumulator()
  const { collectLiveCommittedStateEntries, clearCommittedState } = useCommittedStateAccumulator()

  // Bypassing useAssistantSurface() also bypasses its auth-scope-change cache clear, so it is
  // replicated directly here -- real for Trip Chat via the anonymous-to-member claim flow
  // (anonymousClaimTransfer/waitForAnonymousChatClaim above), the same class of transition
  // useAssistantSurface protects against for offer page. clearSurfaceActivity/clearCommittedState
  // are cleared here too, for the same reason: this transition doesn't dispatch reset_chat_state
  // (that only covers chat switch), so it's the only path that would otherwise miss it.
  const authScope = buildAssistantIdentityScopeKey(memberId)
  const previousAuthScope = usePrevious(authScope, authScope)
  useEffect(() => {
    if (previousAuthScope !== authScope) {
      clearAssistantSurfaceSnapshotRefs()
      clearSurfaceActivity()
      clearCommittedState()
    }
  }, [authScope, previousAuthScope, clearAssistantSurfaceSnapshotRefs, clearSurfaceActivity, clearCommittedState])

  const resolveTurnAuthority = useCallback(async(
    chatId: number,
    accessToken: string | undefined,
    signal?: AbortSignal,
  ): Promise<AssistantOfferPageTurnAuthority | undefined> => {
    // Drained before the try block so it survives even a synchronous buildTripPlannerSnapshot
    // throw -- the drained entries are held in this call's own local variable, not a shared
    // ref, so a second, overlapping resolveTurnAuthority call (submit/regenerate racing before
    // the disabled-button state commits) can never clobber this call's restore target.
    const drainedActivity = drainSurfaceActivity()

    let snapshot: AssistantSurfaceSnapshotBuildResult | undefined
    try {
      snapshot = buildTripPlannerSnapshot({
        chatId,
        turnAuthoritySnapshot: turnAuthoritySnapshotRef.current,
        surfaceActivityEntries: drainedActivity,
        committedStateEntries: collectLiveCommittedStateEntries(),
      })
    } catch(error) {
      // Building the snapshot is the one step here that can throw synchronously
      // (unlike resolveSurfaceSnapshotAuthority, which always resolves) -- mirrors
      // OfferPageAssistantPanel's own resolveTurnAuthority exactly.
      reportLog('[TripPlannerChatProvider] Failed to build trip_planner snapshot; falling back to degraded identity', { error })
      snapshot = undefined
    }

    const resolvedAuthority = await resolveSurfaceSnapshotAuthority(chatId, snapshot, accessToken, signal)
    if (resolvedAuthority.kind === 'snapshot_ref') {
      // Keep the batch attached to this authority until the submit boundary.
      // Publishing a snapshot alone does not mean the customer message was sent.
      return {
        snapshotRef: resolvedAuthority.snapshotRef,
        releaseUnsubmittedActivity: () => restoreDrainedActivity(drainedActivity),
        claimSubmittedActivity: () => claimSubmittedActivity(drainedActivity),
      }
    }

    restoreDrainedActivity(drainedActivity)
    // itineraryData feeds tripId derivation here, not the snapshot builder above -- kept as
    // a real dep (below) even though buildTripPlannerSnapshot itself doesn't need it, so this
    // callback still gets recreated on every itinerary change, but for a genuine reason.
    return { degradedSurfaceIdentity: buildTripPlannerDegradedIdentity({ chatId, itineraryData }) }
  }, [itineraryData, turnAuthoritySnapshotRef, resolveSurfaceSnapshotAuthority, drainSurfaceActivity, restoreDrainedActivity, claimSubmittedActivity, collectLiveCommittedStateEntries])

  // ── Chat core ──

  const messagesWrapperRef = useRef<HTMLDivElement | null>(null)

  const {
    messages,
    filteredMessages,
    userFeedbackMap,
    setUserFeedbackMap,
    isChatError,
    chatError,
    loadingState,
    isStreaming,
    isStreamOpen,
    isAwaitingMessageEnd,
    submitMessage,
    regenerateResponse,
    stopStreaming,
    retryLastMessage,
    handleScroll,
    isAtBottom,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    setFollowTail,
    dynamicSpacerHeight,
    resetConversation,
    resetStreamingState,
    streamError,
    reasoningSummaries,
    toolCallInfo,
    latestGenerationRef,
  } = useChatCore({
    chatId: props.chatId,
    setChatId: props.setChatIdForStreaming,
    isDevMode: props.isDevMode,
    files,
    setFiles,
    isLoggedIn: props.isLoggedIn,
    orderIds: props.orderIds,
    refetchChatList: props.refetchChatList,
    startChatTitleGeneration: props.startChatTitleGeneration,
    updateChatLastActivityTimestamp: props.updateChatLastActivityTimestamp,
    updateChatMetadata: props.updateChatMetadata,
    onItineraryUpdates: handleItineraryUpdates,
    onEnrichmentComplete: handleItineraryEnrichmentComplete,
    onStreamComplete: handleRefetchItinerary,
    onHotelSearchResults,
    onExperienceSearchResults,
    onHotelSearchError,
    onExperienceSearchError,
    onUiAction,
    messagesWrapperRef,
    userData: props.userData,
    surface: 'trip_planner',
    chatKey: props.chatKey,
    resolveTurnAuthority,
  })

  // Sync isStreamingRef after render but before paint — useLayoutEffect eliminates the
  // 1-tick gap that would allow refetchOnWindowFocus to fire during streaming.
  useLayoutEffect(() => {
    isStreamingRef.current = isStreaming
  }, [isStreaming])

  // Itinerary updates arrive mid-turn, so the newest assistant message is the one that
  // caused them. Kept in a ref because useItineraryUpdates runs before the chat core.
  useLayoutEffect(() => {
    activeAssistantMessageIdRef.current = messages.findLast(message => message.role === 'assistant')?.id
  }, [messages])

  // An "Adding to trip" snackbar waits for the itinerary update that replaces it. If the turn
  // ends without one — the assistant asked a question instead of adding — retire it here
  // rather than leaving it spinning until its safety timeout.
  const wasStreamingRef = useRef(false)
  const [lastCompletedTurnId, setLastCompletedTurnId] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      dismissPendingTripPlannerSnackbar()
      clearTripActionTurn()
      // Publishing the finished turn here keeps the signal to once per turn, so surfaces
      // that only need "something new arrived" never subscribe to the streaming context.
      setLastCompletedTurnId(activeAssistantMessageIdRef.current)
    }
    wasStreamingRef.current = isStreaming
  }, [isStreaming, activeAssistantMessageIdRef])

  // The completed turn belongs to the chat it streamed in, so it stops meaning anything
  // once the traveller opens a different one. Adjusted during render rather than in an
  // effect so no consumer ever sees the previous chat's turn.
  const [turnChatId, setTurnChatId] = useState(props.chatId)
  if (turnChatId !== props.chatId) {
    setTurnChatId(props.chatId)
    setLastCompletedTurnId(undefined)
    clearTripActionTurn()
  }

  // Single reset handler — placed after useChatCore so latestGenerationRef is in scope
  // Note: enrichment state (enrichingStopIds/enrichingItemIds) is derived from itinerary data
  // and auto-resets when chatId changes, so no manual reset is needed.
  useEventSubscription('reset_chat_state', () => {
    clearScrollTarget()
    resetPendingExperienceDaySelections()
    latestGenerationRef.current = 0
    setPendingUiAction(undefined)
    setFiles([])
  })

  // ── Chat message senders ──

  const sendMessage = useCallback(async(message: string, options?: SendMessageOptions) => {
    syncPendingExperienceDaySelection(options)

    let enrichedMessage = options?.offerId
      ? `${message}${buildOfferIdContext(options.offerId, options.offerType)}`
      : message

    if (options?.roomOption) {
      enrichedMessage = `${enrichedMessage}${buildRoomOptionContext(options.roomOption, {
        ageThresholds: resolveOccupancyAgeThresholdsForSendMessage(options, getAllOffers(store.getState())),
      })}`
    }

    return submitMessage(enrichedMessage)
  }, [submitMessage, store])

  const sendPostAuthMessage = useCallback(async(signal?: AbortSignal) => {
    await waitForAnonymousChatClaim(signal)
    // System-generated follow-up, not a new user-authored message — bypasses
    // the spoof-send confirmation (see useStreamConnection.handleSubmit).
    return submitMessage('I have logged in.', { bypassSpoofConfirm: true })
  }, [submitMessage, waitForAnonymousChatClaim])

  // ── Memoized context values ──

  const actionsValue = useMemo<ChatActionsValue>(() => ({
    submitMessage,
    sendMessage,
    sendPostAuthMessage,
    regenerateResponse,
    stopStreaming,
    resetConversation,
    resetStreamingState,
    setFollowTail,
    retryLastMessage,
  }), [submitMessage, sendMessage, sendPostAuthMessage, regenerateResponse, stopStreaming, resetConversation, resetStreamingState, setFollowTail, retryLastMessage])

  const statusValue = useMemo<ChatStreamStatusValue>(() => ({
    chatId: props.chatId,
    chatKey: props.chatKey,
    isStreaming,
    isStreamOpen,
    isAwaitingMessageEnd,
    loadingState,
    streamError,
    isOnline,
    reasoningSummaries,
    toolCallInfo,
  }), [props.chatId, props.chatKey, isStreaming, isStreamOpen, isAwaitingMessageEnd, loadingState, streamError, isOnline, reasoningSummaries, toolCallInfo])

  const scrollValue = useMemo<ChatScrollValue>(() => ({
    handleScroll,
    isAtBottom,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    dynamicSpacerHeight,
    messagesWrapperRef,
  }), [handleScroll, isAtBottom, hasContentOutOfView, enableFollowAndScrollToBottom, dynamicSpacerHeight, messagesWrapperRef])

  const turnCompletionValue = useMemo<ChatTurnCompletionValue>(() => ({
    lastCompletedTurnId,
  }), [lastCompletedTurnId])

  const uiActionValue = useMemo<ChatUiActionValue>(() => ({
    pendingUiAction,
    clearPendingUiAction,
  }), [pendingUiAction, clearPendingUiAction])

  const viewAddedItem = useCallback(() => {
    if (!addedItemLink) return
    eventBus.dispatch({
      type: 'view_itinerary_item',
      stopId: addedItemLink.stopId,
      dayId: addedItemLink.dayId,
      itemId: addedItemLink.itemId,
    })
  }, [addedItemLink, eventBus])

  const addedItineraryItemValue = useMemo<AddedItineraryItemContextValue>(() => ({
    addedItemLink,
    viewAddedItem,
  }), [addedItemLink, viewAddedItem])

  const streamMessagesValue = useMemo<ChatStreamMessagesValue>(() => ({
    messages,
    filteredMessages,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
  }), [messages, filteredMessages, isChatError, chatError, userFeedbackMap, setUserFeedbackMap])

  // Staff-only rewind. When a snapshot is being inspected it replaces the live itinerary so
  // the whole panel time-travels; nothing is mutated and live data returns on exit.
  // The finalized assistant turns (oldest → newest) are the states the stepper walks —
  // this must match the turns MessagesList renders per-message rewind controls on.
  const assistantMessageIds = useMemo(
    () => filteredMessages.filter(message => message.role === 'assistant' && message.isFinalized).map(message => message.id),
    [filteredMessages],
  )
  const staffRewind = useStaffRewindValue({
    chatId: props.chatId,
    region: currentRegionCode || props.userData.region || 'AU',
    canStaffRewind: props.canStaffRewind,
    assistantMessageIds,
    navigateToChat: props.navigateToChat,
  })
  const effectiveItineraryData = staffRewind.snapshotItinerary ?? itineraryData

  const hasItineraryData = Boolean(effectiveItineraryData?.stops?.length)

  // A rewind snapshot is already resolved, so it never waits on the itinerary query.
  const isItineraryInitialLoading = !staffRewind.snapshotItinerary && isItineraryLoading

  const multiCartId = effectiveItineraryData?.multiCartId

  const { mutateAsync: deleteManualItineraryTargetMutation } = useManualItineraryDeleteMutation()
  const { mutateAsync: moveManualItineraryItemsMutation } = useManualItineraryMoveMutation()
  const [isManualItineraryDeletePending, setIsManualItineraryDeletePending] = useState(false)
  const isManualItineraryDeletePendingRef = useRef(false)
  const [isManualItineraryMovePending, setIsManualItineraryMovePending] = useState(false)
  const isManualItineraryMovePendingRef = useRef(false)
  const { isFlightCartMutationPending, beginFlightCartMutation, captureCartOperationScope } = useChatCartMutationState(props.chatId, props.isLoggedIn, authScope)

  const { reconcileCartForCheckout } = useTripChatCartReconciliation({
    chatId: props.chatId,
    itineraryData,
    isLoggedIn: props.isLoggedIn,
    isStreaming,
    isStreamOpen,
    isManualItineraryDeletePending,
    isManualItineraryMovePending,
    isFlightCartMutationPending,
    waitForAnonymousChatClaim,
    anonymousClaimTransfer: props.anonymousClaimTransfer,
  })

  const deleteManualItineraryTarget = useCallback(async(
    target: ManualItineraryDeleteRequest,
    displayName: string,
  ): Promise<boolean> => {
    if (isStreaming || isStreamOpen || isManualItineraryDeletePendingRef.current || isManualItineraryMovePendingRef.current) return false

    const deleteFailedMessage = intl.formatMessage({
      id: 'f5L_AqJEDPR7oRx_l8lK',
      defaultMessage: 'Unable to delete from itinerary. Please try again.',
      description: 'Error snackbar after manually deleting an item, stop, or day from a trip planner itinerary fails',
    })

    if (!props.chatId) {
      showTripActionFailedSnackbar(intl, deleteFailedMessage)
      return false
    }

    isManualItineraryDeletePendingRef.current = true
    setIsManualItineraryDeletePending(true)
    // The traveller tapped delete, so we know the target the moment they commit; the
    // confirmation below replaces this rather than the traveller waiting on a dead UI.
    showTripActionPendingSnackbar(intl, 'delete', displayName)

    try {
      const result = await deleteManualItineraryTargetMutation({
        chatId: props.chatId,
        region: currentRegionCode || props.userData.region || 'AU',
        target,
      })
      if (result.conversationContextSynced) {
        // No View action: there is nothing left to look at.
        showTripActionConfirmedSnackbar(intl, 'delete', displayName)
      } else {
        showTripActionFailedSnackbar(intl, intl.formatMessage({
          id: '_pHdot57rf1emFxKUA-B',
          defaultMessage: 'Removed from itinerary, but AI context could not sync. Please refresh before continuing.',
          description: 'Critical snackbar after a manual itinerary delete succeeds but the AI conversation context update fails',
        }), 'warning')
      }
      // Real deletion succeeded either way -- conversationContextSynced tracks whether the AI's
      // own conversation state also picked it up, not whether the cart/itinerary mutation itself
      // happened. Mirrors moveManualItineraryItems's own success/failure return below.
      return true
    } catch {
      showTripActionFailedSnackbar(intl, deleteFailedMessage)
      return false
    } finally {
      isManualItineraryDeletePendingRef.current = false
      setIsManualItineraryDeletePending(false)
      clearTripActionTurn()
    }
  }, [isStreaming, isStreamOpen, props.chatId, props.userData.region, currentRegionCode, deleteManualItineraryTargetMutation, intl])

  const moveManualItineraryItems = useCallback(async(request: ManualItineraryMoveRequest) => {
    const moveFailedMessage = intl.formatMessage({
      id: 'niZexKiXSQvQU6UwoSLi',
      defaultMessage: 'Unable to move itinerary item. Please try again.',
      description: 'Error snackbar after manually moving an item to another day in a trip planner itinerary fails',
    })
    const moveCount = request.moves.length
    if (isStreaming || isStreamOpen || isManualItineraryDeletePendingRef.current || isManualItineraryMovePendingRef.current) return false

    if (!props.chatId) {
      showTripActionFailedSnackbar(intl, moveFailedMessage)
      return false
    }

    isManualItineraryMovePendingRef.current = true
    setIsManualItineraryMovePending(true)
    // Only the ids are known before the response, so the pending toast counts rather than
    // names; the confirmation below upgrades to the real name when exactly one item moved.
    showTripActionPendingSnackbar(intl, 'update', intl.formatMessage(MOVED_ITEM_COUNT, { moveCount }))

    // Captured before the move request round-trips below, not after -- shared by every move call
    // site (canvas drag, list drag, list menu, bulk), same as the entry itself.
    const occurredAt = new Date().toISOString()

    try {
      const result = await moveManualItineraryItemsMutation({
        chatId: props.chatId,
        region: currentRegionCode || props.userData.region || 'AU',
        request,
      })

      if (!result.success) {
        showTripActionFailedSnackbar(intl, intl.formatMessage({
          id: '44SsvAemYy2YP1WNK08K',
          defaultMessage: 'Some itinerary items could not be moved. Please try again.',
          description: 'Error snackbar after one or more manually moved trip planner itinerary items fail',
        }))
        return false
      }

      // Built entirely from the response's own successes -- itemName/dayLabel only exist there,
      // never in the request -- so every move call site (canvas drag, list drag, list menu, bulk)
      // gets this for free with no new plumbing.
      const movedActivityEntry = buildItemMovedActivityEntry(result.successes, 'trip_planner.itinerary.move', occurredAt)
      if (movedActivityEntry) {
        eventBus.dispatch({
          type: 'trip_planner_surface_activity',
          entries: [movedActivityEntry],
        })
      }

      const movedName = result.successes.length === 1
        ? result.successes[0].itemName
        : undefined
      const movedDescription = movedName ?? intl.formatMessage(MOVED_ITEM_COUNT, { moveCount })

      if (result.conversationContextSynced) {
        showTripActionConfirmedSnackbar(intl, 'update', movedDescription)
      } else {
        showTripActionFailedSnackbar(intl, intl.formatMessage(
          {
            id: 'd90mGpew2mJSD8IuvHiz',
            defaultMessage: 'Moved {moveCount, plural, one {itinerary item} other {itinerary items}}, but AI context could not sync. Please refresh before continuing.',
            description: 'Critical snackbar after one or more manual itinerary moves succeed but AI conversation context sync fails',
          },
          { moveCount },
        ), 'warning')
      }
      return true
    } catch {
      showTripActionFailedSnackbar(intl, moveFailedMessage)
      return false
    } finally {
      isManualItineraryMovePendingRef.current = false
      setIsManualItineraryMovePending(false)
      clearTripActionTurn()
    }
  }, [isStreaming, isStreamOpen, props.chatId, props.userData.region, currentRegionCode, moveManualItineraryItemsMutation, intl, eventBus])

  const itineraryValue = useMemo<ItineraryContextValue>(() => ({
    itineraryData: effectiveItineraryData,
    hasItineraryData,
    isItineraryLoading: isItineraryInitialLoading,
    multiCartId,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    refetchCurrentChatItinerary,
    deleteManualItineraryTarget,
    moveManualItineraryItems,
    isManualItineraryDeletePending,
    isManualItineraryMovePending,
    isFlightCartMutationPending,
    beginFlightCartMutation,
    captureCartOperationScope,
    reconcileCartForCheckout,
  }), [
    effectiveItineraryData, hasItineraryData, isItineraryInitialLoading, multiCartId,
    enrichingStopIds, enrichingItemIds,
    scrollTarget, clearScrollTarget,
    refetchCurrentChatItinerary, deleteManualItineraryTarget, moveManualItineraryItems,
    isManualItineraryDeletePending, isManualItineraryMovePending, isFlightCartMutationPending, beginFlightCartMutation, captureCartOperationScope,
    reconcileCartForCheckout,
  ])

  const filesValue = useMemo<ChatFilesValue>(() => ({
    files,
    setFiles,
  }), [files, setFiles])

  return (
    <ChatActionsContext.Provider value={actionsValue}>
      <ChatStreamStatusContext.Provider value={statusValue}>
        <ChatScrollContext.Provider value={scrollValue}>
          <ChatFilesContext.Provider value={filesValue}>
            <ChatUiActionContext.Provider value={uiActionValue}>
              <ChatTurnCompletionContext.Provider value={turnCompletionValue}>
                <ChatStreamMessagesContext.Provider value={streamMessagesValue}>
                  <ItineraryContext.Provider value={itineraryValue}>
                    <AddedItineraryItemContext.Provider value={addedItineraryItemValue}>
                      <StaffRewindContext.Provider value={staffRewind}>
                        {children}
                      </StaffRewindContext.Provider>
                    </AddedItineraryItemContext.Provider>
                  </ItineraryContext.Provider>
                </ChatStreamMessagesContext.Provider>
              </ChatTurnCompletionContext.Provider>
            </ChatUiActionContext.Provider>
          </ChatFilesContext.Provider>
        </ChatScrollContext.Provider>
      </ChatStreamStatusContext.Provider>
    </ChatActionsContext.Provider>
  )
}
